import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const IDLE_LIMIT_MS = 15 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000;
const WARNING_TOAST_ID = "idle-warning";

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"] as const;

export function useIdleLogout(enabled: boolean) {
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loggedOutRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    loggedOutRef.current = false;

    const clearTimers = () => {
      if (warningTimer.current) clearTimeout(warningTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      warningTimer.current = null;
      logoutTimer.current = null;
    };

    const doLogout = async () => {
      if (loggedOutRef.current) return;
      loggedOutRef.current = true;
      clearTimers();
      toast.dismiss(WARNING_TOAST_ID);
      await supabase.auth.signOut();
      toast.error("Signed out due to inactivity.");
      window.location.href = "/login";
    };

    const showWarning = () => {
      toast.warning("You'll be signed out in 1 minute due to inactivity. Tap anywhere to stay signed in.", {
        id: WARNING_TOAST_ID,
        duration: WARNING_BEFORE_MS,
      });
    };

    const resetTimers = () => {
      if (loggedOutRef.current) return;
      clearTimers();
      toast.dismiss(WARNING_TOAST_ID);
      warningTimer.current = setTimeout(showWarning, IDLE_LIMIT_MS - WARNING_BEFORE_MS);
      logoutTimer.current = setTimeout(doLogout, IDLE_LIMIT_MS);
    };

    const handleActivity = () => resetTimers();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") resetTimers();
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", handleVisibility);

    resetTimers();

    return () => {
      clearTimers();
      toast.dismiss(WARNING_TOAST_ID);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled]);
}
